import React from 'react';
import { Card, Typography, Button, Input, Flex, theme, Space, Grid, Divider, List } from 'antd';
import { CopyOutlined, LayoutOutlined } from '@ant-design/icons';
import { useAssistant } from '../../../../../contexts/AssistantContext';
import config from '../../../../../config';
import { getIntegrationCode, handleCopy } from '../../../utils';
import { WIDGET_DATA } from '../../../constants';

const { Title, Text, Paragraph } = Typography;
const { useToken } = theme;
const { useBreakpoint } = Grid;

// Componente para el encabezado del widget
const WidgetHeader: React.FC<{ item: typeof WIDGET_DATA[0]; isMobile: boolean; token: any }> = ({ 
  item, 
  isMobile, 
  token 
}) => (
  <List.Item>
    <Flex
      justify="space-between"
      align={isMobile ? 'flex-start' : 'center'}
      style={{ width: '100%' }}
      vertical={isMobile}
      gap={isMobile ? token.marginSM : 0}
    >
      <Flex
        align="start"
        gap={token.marginMD}
        style={{ width: isMobile ? '100%' : 'auto' }}
      >
        {item.iconType === 'layout' && <LayoutOutlined style={item.iconStyle} />}
        <Flex vertical gap={'8px'}>
          <Text strong style={{ fontSize: '17px' }}>{item.title}</Text>
          <Paragraph type="secondary" style={{ margin: 0 }}>
            {item.description}
          </Paragraph>
          <Text type="secondary">Setup: {item.setup}</Text>
        </Flex>
      </Flex>
      <Button style={{ width: isMobile ? '100%' : 'auto' }}>
        {item.action}
      </Button>
    </Flex>
  </List.Item>
);

// Componente para el código de integración
const IntegrationCode: React.FC<{ code: string; isMobile: boolean; token: any }> = ({ code, isMobile, token }) => (
  <Space.Compact
    style={{
      width: '100%',
      display: isMobile ? 'flex' : 'inline-flex',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: 'center',
      gap: token.marginXS,
    }}
  >
    <Flex style={{
      background: 'var(--ant-colorIconWidget)',
      borderRadius: '8px',
      width: '100%',
      justifyContent: 'space-around',
      alignItems: 'center',
      padding: '10px 0'
    }}>
      <Input
        readOnly
        value={code}
        style={{
          background: 'transparent',
          border: 'none',
          color: '#ffffff',
          padding: '20px',
        }}
      />
      <Button
        icon={<CopyOutlined />}
        onClick={() => handleCopy(code)}
        style={{
          width: 'auto',
          borderRadius: '8px',
          marginRight: '20px'
        }}
        id="copy-web-config-button"
      >
        Copy code
      </Button>
    </Flex>
  </Space.Compact>
);

// Componente para los pasos de instalación
const InstallationSteps: React.FC = () => (
  <Card style={{ background: 'var(--ant-colorPrimaryBgHover)' }}>
    <Title level={5} style={{ marginTop: '0.5rem' }}>Installation Steps:</Title>
    <ol>
      <li>Copy the code above</li>
      <li>Paste it just before the closing &lt;/body&gt; tag</li>
      <li>Save and publish your changes</li>
    </ol>
  </Card>
);

// Componente para la verificación de instalación
const InstallationVerification: React.FC<{ isMobile: boolean }> = ({ isMobile }) => (
  <Space.Compact
    style={{
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: '10px'
    }}
  >
    <Title level={5} style={{ marginTop: '0.5rem' }}>Check installation</Title>
    <Paragraph type="secondary">
      Go to any page where the messenger is installed, and you'll see it appear in the bottom
      right corner. Alternatively, you can verify your installation below.
    </Paragraph>
    <Button style={{ width: isMobile ? '100%' : '180px', borderRadius: '8px' }}>
      Check installation
    </Button>
  </Space.Compact>
);

// Componente principal
export default function MyAssistantWebConfig() {
  const { token } = useToken();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const { currentAssistant } = useAssistant();

  const integrationCode = getIntegrationCode(config.app.domain, currentAssistant?.id);

  return (
    <Card style={{ maxWidth: 1200, margin: 'auto', border: 'none' }}>
      <Flex vertical gap={token.marginLG}>
        <List
          itemLayout="horizontal"
          dataSource={[WIDGET_DATA[0]]}
          renderItem={(item) => (
            <WidgetHeader item={item} isMobile={isMobile} token={token} />
          )}
        />
        <Divider style={{ padding: '0px', margin: '0px' }} />
        
        <Flex vertical gap={token.marginSM}>
          <Title level={5} style={{ marginTop: '0.5rem' }}>Website Widget</Title>
          <IntegrationCode code={integrationCode} isMobile={isMobile} token={token} />
          <InstallationSteps />
          <Divider style={{ padding: '0px', margin: '10px 0' }} />
          <InstallationVerification isMobile={isMobile} />
        </Flex>
      </Flex>
    </Card>
  );
}

// Default Ant Design styling has been used. For custom theming, visit https://ant.design/docs/react/customize-theme